import React, {
  Fragment,
  useEffect,
  useRef,
  type ReactNode,
  type JSX,
} from 'react';
import { createPortal } from 'react-dom';
import { styled } from 'styled-components';
import copyToClipboard from 'copy-to-clipboard';
import { createPopper } from '@popperjs/core';
import { headerBg, headerTextHover, captionSmall } from 'r/Design';
import { useStateReducer, getIsMobile } from 'r/utils';
import { zHoverTooltip } from 'r/constants/zIdxs';

const Tooltip = styled.div`
  background: ${headerBg};
  border-radius: 3px;
  color: ${headerTextHover};
  pointer-events: none;
  visibility: ${(props) => (props.$isHidden ? 'hidden' : 'visible')};
  z-index: ${zHoverTooltip};
  padding: 4px 10px;
  text-align: center;
`;

const TooltipContent = styled.div`
  ${captionSmall}
`;

export default function WithHoverTooltip({
  element,
  tooltipContent,
  copyValue,
}: {
  copyValue?: string;
  element: React.ReactElement<
    JSX.IntrinsicElements[keyof JSX.IntrinsicElements]
  >;
  tooltipContent: ReactNode;
}) {
  const [{ isCopiedTooltipShown, isTooltipShown }, ss] = useStateReducer({
    isCopiedTooltipShown: false,
    isTooltipShown: false,
  });

  const popperRef = useRef();
  const copiedPopperRef = useRef();
  const wrRef = useRef();
  const tooltipRef = useRef();
  const copiedTooltipRef = useRef();
  const copiedTimeoutIdRef = useRef();

  useEffect(() => {
    if (isTooltipShown) {
      popperRef.current = createPopper(wrRef.current, tooltipRef.current, {
        placement: 'top',
        modifiers: [
          {
            name: 'offset',
            options: { offset: [0, 8] },
          },
        ],
      });
    }
    if (isTooltipShown || isCopiedTooltipShown) {
      copiedPopperRef.current = createPopper(
        wrRef.current,
        copiedTooltipRef.current,
        {
          placement: 'top',
          modifiers: [
            {
              name: 'offset',
              options: { offset: [0, 8] },
            },
          ],
        },
      );
    }
    return () => {
      if (isTooltipShown) {
        popperRef.current.destroy();
      }
      if (isTooltipShown || isCopiedTooltipShown) {
        copiedPopperRef.current.destroy();
      }
    };
  }, [isTooltipShown, isCopiedTooltipShown]);

  const isMobile = getIsMobile();

  useEffect(() => {
    if (isMobile) {
      const f = (e) => {
        if (!wrRef.current.contains(e.target)) {
          ss({ isTooltipShown: false, isCopiedTooltipShown: false });
          clearTimeout(copiedTimeoutIdRef.current);
        }
      };
      document.addEventListener('touchstart', f);
      return () => document.removeEventListener('touchstart', f);
    }
  });

  const elementProps = {
    ref: wrRef,
  };

  if (isMobile) {
    elementProps.onClick = (e) => {
      e.stopPropagation();
      if (!isTooltipShown) {
        clearTimeout(copiedTimeoutIdRef.current);
        ss({ isTooltipShown: true, isCopiedTooltipShown: false });
        return;
      }
      if (copyValue) {
        copyToClipboard(copyValue);
        clearTimeout(copiedTimeoutIdRef.current);
        ss({ isTooltipShown: false, isCopiedTooltipShown: true });
        copiedTimeoutIdRef.current = setTimeout(
          () => ss({ isTooltipShown: false, isCopiedTooltipShown: false }),
          2000,
        );
      }
    };
  } else {
    elementProps.onMouseEnter = () => {
      if (element.props.onMouseEnter) {
        element.props.onMouseEnter();
      }
      ss({ isTooltipShown: true, isCopiedTooltipShown: false });
    };

    elementProps.onMouseLeave = () => {
      if (element.props.onMouseLeave) {
        element.props.onMouseLeave();
      }
      clearTimeout(copiedTimeoutIdRef.current);
      ss({ isTooltipShown: false, isCopiedTooltipShown: false });
    };

    if (copyValue) {
      elementProps.onClick = (e) => {
        e.stopPropagation();
        copyToClipboard(copyValue);
        clearTimeout(copiedTimeoutIdRef.current);
        ss({ isTooltipShown: false, isCopiedTooltipShown: true });
        copiedTimeoutIdRef.current = setTimeout(() => {
          ss({ isCopiedTooltipShown: false });
        }, 2000);
      };
    }
  }

  if (copyValue) {
    elementProps.style = { cursor: 'pointer' };
  }

  return (
    <>
      {isTooltipShown &&
        createPortal(
          <Tooltip ref={tooltipRef}>
            {typeof tooltipContent === 'string' ?
              <TooltipContent>{tooltipContent}</TooltipContent>
            : Array.isArray(tooltipContent) ?
              <TooltipContent>
                {tooltipContent.map((str, idx) => (
                  <Fragment key={idx}>
                    {str}
                    <br />
                  </Fragment>
                ))}
              </TooltipContent>
            : tooltipContent}
          </Tooltip>,
          document.body,
        )}
      {(isTooltipShown || isCopiedTooltipShown) &&
        createPortal(
          <Tooltip ref={copiedTooltipRef} $isHidden={!isCopiedTooltipShown}>
            <TooltipContent>Copied</TooltipContent>
          </Tooltip>,
          document.body,
        )}
      {React.cloneElement(element, { ...element.props, ...elementProps })}
    </>
  );
}
